import { useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Badge } from '@/components/ui/badge';
import { authApi } from '@/lib/pillarApi';
import { runInBackground } from '@/lib/utils';
import { useAuthStore } from '@/store/auth';
import { ActiveSessionsCard } from '@/components/profile/active-sessions-card';
import { SessionListItem } from '@pillar-portal/api-types';
import { createComponentLogger } from '@/lib/logging/frontend-logger';

interface SessionsManagementCardProps {
    mode: 'admin' | 'user';
    showSearch: boolean;
    title?: string;
    description?: string;
    showHeader: boolean;
    refetchInterval?: number;
}

export function SessionsManagementCard({
    mode,
    showSearch = false,
    title = 'Active Sessions',
    description = 'View and manage active user sessions',
    showHeader = true,
    refetchInterval = 30000,
}: SessionsManagementCardProps) {
    const log = createComponentLogger('SessionsManagementCard');
    const queryClient = useQueryClient();
    const currentUser = useAuthStore((state) => state.user);
    const setEnableJavascriptConsole = useAuthStore((state) => state.setEnableJavascriptConsole);

    const isAdminMode = mode === 'admin';

    const { data, isLoading, error } = useQuery({
        queryKey: [isAdminMode ? 'adminSessions' : 'mySessions'],
        queryFn: async () => await (isAdminMode ? authApi.getAllSessions() : authApi.getSessions()),
        ...(isAdminMode && { refetchInterval }),
    });

    const terminateSessionMutation = useMutation({
        mutationFn: (session: SessionListItem) =>
            isAdminMode
                ? authApi.expireSessionAdmin({
                      session_uuid: (() => {
                          if (session.session_uuid === undefined) {
                              throw new Error('Missing session_uuid for admin session termination');
                          }
                          return session.session_uuid;
                      })(),
                      user_uuid: (() => {
                          if (session.user_uuid === undefined) {
                              throw new Error('Missing user_uuid for admin session termination');
                          }
                          return session.user_uuid;
                      })(),
                  })
                : (() => {
                      if (session.session_uuid === undefined) {
                          throw new Error('Missing session_uuid for session termination');
                      }
                      return authApi.expireSession({
                          session_uuid: session.session_uuid,
                      });
                  })(),
        onSuccess: () => {
            runInBackground(
                queryClient.invalidateQueries({
                    queryKey: [isAdminMode ? 'adminSessions' : 'mySessions'],
                }),
                (err) => {
                    log.error('Error invalidating queries', err);
                }
            );
        },
    });

    const updateConsoleMutation = useMutation({
        mutationFn: (payload: { session: SessionListItem; enabled: boolean }) =>
            authApi.setSessionConsoleAdmin({
                session_uuid: (() => {
                    if (payload.session.session_uuid === undefined) {
                        throw new Error('Missing session_uuid for session console updates');
                    }
                    return payload.session.session_uuid;
                })(),
                enable_javascript_console: payload.enabled,
            }),
        onSuccess: () => {
            runInBackground(
                queryClient.invalidateQueries({
                    queryKey: [isAdminMode ? 'adminSessions' : 'mySessions'],
                }),
                (err) => {
                    log.error('Error invalidating queries after console update', err);
                }
            );
        },
    });

    const sessions = data?.sessions || [];

    const handleTerminateSession = (session: SessionListItem) => {
        terminateSessionMutation.mutate(session);
    };

    const handleToggleConsole = (session: SessionListItem, enabled: boolean) => {
        updateConsoleMutation.mutate({ session, enabled });
    };

    useEffect(() => {
        const currentSession = sessions.find((session) => session.is_current);
        if (currentSession) {
            setEnableJavascriptConsole(currentSession.enable_javascript_console);
        }
    }, [sessions, setEnableJavascriptConsole]);

    if (!currentUser) {
        return null;
    }

    return (
        <div>
            {showHeader && (
                <div className="flex items-center justify-between mb-8">
                    <h1 className="text-3xl font-bold">{title}</h1>
                    <Badge variant="secondary">{sessions.length} Active Sessions</Badge>
                </div>
            )}

            <ActiveSessionsCard
                sessions={sessions}
                isLoading={isLoading}
                error={error instanceof Error ? error : null}
                onTerminateSession={handleTerminateSession}
                isTerminating={terminateSessionMutation.isPending}
                onToggleConsoleLogging={isAdminMode ? handleToggleConsole : undefined}
                isTogglingConsole={isAdminMode ? updateConsoleMutation.isPending : false}
                mode={mode}
                showSearch={showSearch}
                {...(!showHeader && title && { title })}
                description={description}
            />
        </div>
    );
}
